import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import moment from 'moment';

import type {FilterObject} from 'nutshell-core/types';

import {PageControlBar} from 'shells/control-bar/page-control-bar';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {SmsIcon} from 'shells/icon/svg/sms-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {SmsBroadcastFragment as Broadcast} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../../../layout';
import type {ReportURLParams} from '../../../reports/report-types';

import {getEditionStatusBarInfo} from '../../editions/edition-helpers';
import {RescheduleEditionModal} from '../../editions/prepare-to-send-modal/reschedule-edition-modal';
import {EditionRecipientList} from '../../editions/recipient-list/edition-recipient-list';

import {EditionEngagement} from '../engagement/edition-engagement';
import {editionFieldFilterFunction} from '../recipient-list/helpers';
import {SmsEditionRecipientEngagementFilter} from '../recipient-list/sms-edition-recipient-engagement-filter';

import {BroadcastDetails} from './broadcast-details';
import {
    useUpdateSMSBroadcast,
    useScheduleSmsEdition,
    useUnscheduleSmsEdition,
} from './graphql/hooks';

type Props = {
    activeTab: string;
    generatePath: (newTab: string) => string;
    broadcast: Broadcast | null | undefined;
    isLoading: boolean;
    setIsScheduling: (arg1: boolean) => void;
    setIsRescheduling: (arg1: boolean) => void;
    isRescheduling: boolean;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

export function BroadcastPage(props: Props) {
    const {broadcast} = props;

    const {user} = useGetSessionUser();

    const {updateSMSBroadcast} = useUpdateSMSBroadcast();
    const {scheduleSmsEdition} = useScheduleSmsEdition();
    const {unscheduleSmsEdition} = useUnscheduleSmsEdition();

    const isOwner = Boolean(user && broadcast && broadcast.owner && broadcast.owner.id === user.id);

    const title = broadcast && broadcast.edition ? broadcast.edition.name : 'Broadcast';

    const tabOptions = [
        {
            id: 'details',
            label: 'Details',
            icon: SmsIcon,
            href: props.generatePath('details'),
            component: broadcast ? (
                <BroadcastDetails
                    broadcast={broadcast}
                    prepareToSendPath={props.generatePath('prepare')}
                    onUpdate={updateSMSBroadcast}
                />
            ) : (
                <LoadingPage />
            ),
        },
        {
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: props.generatePath('engagement'),
            component: broadcast && broadcast.edition && (
                <EditionEngagement edition={broadcast.edition} />
            ),
        },
    ];

    // Currently only showing recipient list after the edition is sent
    if (
        broadcast &&
        (broadcast.edition.status === 'SENT' || broadcast.edition.status === 'HALTED')
    ) {
        tabOptions.push({
            id: 'recipients',
            label: 'Recipients',
            icon: AudienceIcon,
            href: props.generatePath('recipients'),
            component: (
                <EditionRecipientList
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    edition={broadcast.edition}
                    campaignType='BROADCAST'
                    graduatedFilters={
                        <SmsEditionRecipientEngagementFilter
                            editionId={broadcast.edition.id}
                            filters={props.activeFilters}
                            onParamChange={props.onParamChange}
                            urlParams={props.urlParams}
                        />
                    }
                    fieldFilterFunction={editionFieldFilterFunction}
                />
            ),
        });
    }

    return (
        <PageContainer
            title={title}
            pageTitleBar={
                <PageTitleBar
                    title={title}
                    subheaderPrimaryText=''
                    buttons={
                        broadcast &&
                        broadcast.edition.status === 'NEW' &&
                        !broadcast.edition.deletedTime &&
                        props.activeTab !== 'engagement' && (
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                disabled={!props.broadcast}
                                variant='button-primary'
                                size='big'
                                to={props.generatePath('prepare')}
                            >
                                Prepare to send
                            </Link>
                        )
                    }
                    breadcrumbs={[{to: '/email-marketing/sms/broadcasts', text: 'Broadcasts'}]}
                    editInPlaceProps={{
                        onChange: (newValue: string | null | undefined) => {
                            return broadcast
                                ? updateSMSBroadcast({
                                      editionId: broadcast.edition.id,
                                      name: newValue,
                                  })
                                : undefined;
                        },
                        placeholder: 'Name this broadcast',
                    }}
                />
            }
            controlBar={
                <PageControlBar
                    statusInfo={
                        broadcast && broadcast.edition
                            ? getEditionStatusBarInfo(
                                  broadcast.edition,
                                  Boolean(user && user.permissions.canAccessFullCampaigns),
                                  () => {
                                      if (broadcast.edition.status === 'SCHEDULED') {
                                          props.setIsRescheduling(true);
                                      } else {
                                          props.setIsScheduling(true);
                                      }
                                  },
                                  props.isLoading,
                                  isOwner
                              )
                            : undefined
                    }
                    tabs={{selectedTab: props.activeTab, options: tabOptions}}
                />
            }
        >
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={props.activeTab} tabOptions={tabOptions} />
            {broadcast ? (
                <RescheduleEditionModal
                    isOpen={props.isRescheduling}
                    onClose={() => {
                        props.setIsRescheduling(false);
                    }}
                    scheduledTime={broadcast.edition.scheduledTime}
                    onSchedule={(scheduledTime: moment.Moment) => {
                        return scheduleSmsEdition({
                            editionId: broadcast.edition.id,
                            scheduledTime: scheduledTime.unix(),
                        });
                    }}
                    onUnschedule={() => {
                        return unscheduleSmsEdition({
                            editionId: broadcast.edition.id,
                        });
                    }}
                    type={'BROADCAST'}
                />
            ) : undefined}
        </PageContainer>
    );
}
